import axios from "axios";

/**
 * Single owner of the auth session: where tokens live, how a new access token is obtained, and how a
 * session ends.
 *
 * Every call in here uses the bare `axios` import on purpose, never the shared `axiosInstance`.
 * Refresh and logout must not pass through the response interceptor — a 401 on the refresh call
 * would otherwise re-enter the interceptor and try to refresh itself, forever.
 */

// Resolved here rather than in AxiosInterceptor so this module has no imports of its own.
// AxiosInterceptor re-exports it as `imgUrl`, so the ~58 existing importers are unaffected.
export const apiBaseUrl = process.env.REACT_APP_API_URL_PROD
  ? process.env.REACT_APP_API_URL_PROD
  : process.env.REACT_APP_API_URL_STAGING
  ? process.env.REACT_APP_API_URL_STAGING
  : "https://api-staging.areakart.in";

export const ACCESS_TOKEN_KEY = "accessToken";
export const REFRESH_TOKEN_KEY = "refreshToken";
/** Areakart feature codes the store is entitled to, as sent by login/refresh. */
export const SERVICE_CODES_KEY = "areakartServiceCodes";

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

/** Feature codes currently entitled for the logged-in session. */
export const getServiceCodes = () => {
  try {
    const raw = localStorage.getItem(SERVICE_CODES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Areakart's own admin, as opposed to a store user. Entitlements are a per-store subscription
 * concept, so an admin session carries none — read the role rather than the codes.
 */
export const isAdminSession = () => {
  try {
    const raw = localStorage.getItem("userData");
    if (!raw) return false;
    return JSON.parse(raw).roles?.[0]?.name === "Super User";
  } catch {
    return false;
  }
};

/**
 * Whether the session includes the given entitlement code.
 *
 * Admins always pass: they administer every store from the same panel and hold no subscription of
 * their own, so gating them on entitlement codes locked them out of features they own (the AI
 * inventory button showed "Upgrade the subscription to use this service" to Areakart staff).
 */
export const hasServiceCode = (code) =>
  isAdminSession() || (Boolean(code) && getServiceCodes().includes(code));

/** Whether the session includes any of the given entitlement codes. */
export const hasAnyServiceCode = (...codes) =>
  codes.some((code) => hasServiceCode(code));

// Re-export entitlement code constants so callers can import helpers + codes together.
export { AREAKART_SERVICE_CODES } from "./areakartServiceCodes";
export { default as areakartServiceCodes } from "./areakartServiceCodes";

/**
 * Persists whatever an auth response returned. Called on login and again on every refresh, so the
 * service codes cannot drift from the token they arrived with.
 */
export const storeAuthPayload = (payload = {}) => {
  if (payload.accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, payload.accessToken);
  if (payload.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, payload.refreshToken);
  if (Array.isArray(payload.areakartServiceCodes)) {
    localStorage.setItem(SERVICE_CODES_KEY, JSON.stringify(payload.areakartServiceCodes));
  }
};

/**
 * Exchanges the refresh token for a fresh access token.
 * Resolves with the response payload, or null when the session can no longer be renewed.
 */
export const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const res = await axios.post(`${apiBaseUrl}/token/refresh`, { refreshToken });
    if (!res?.data?.accessToken) return null;
    storeAuthPayload(res.data);
    return res.data;
  } catch (e) {
    return null;
  }
};

/**
 * Ends the session server-side so the refresh token can no longer mint access tokens, then clears
 * local state. Best-effort: if the network call fails the local session is still torn down, because
 * refusing to log out locally is worse than leaving one token to expire on its own.
 */
export const endSession = async () => {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await axios.post(`${apiBaseUrl}/token/logout`, { refreshToken });
    } catch (e) {
      // ignore — clearing local state below is what actually logs the user out
    }
  }
  localStorage.clear();
};

/** Clears local state and sends the user to the login screen. */
export const redirectToLogin = () => {
  localStorage.clear();
  window.location.href = `${process.env.PUBLIC_URL}/login`;
};
